import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { VatRateHistoryClient, VatRateHistoryDTO } from '../services/apiClient';
import { API_BASE_URL, MAX_DROPDOWN_PAGE_SIZE } from '../config/config';
import axiosInstance from '../services/axiosInstance';

const apiClient = new VatRateHistoryClient(API_BASE_URL, axiosInstance);

// Cached query for dropdowns
export const useVatRateHistories = () => {
    return useQuery({
        queryKey: ['vatRateHistories'],
        queryFn: async () => {
            const response = await apiClient.searchVatRateHistories({
                page: 1,
                pageSize: MAX_DROPDOWN_PAGE_SIZE,
                sortBy: 'EffectiveDate',
            });
            return response?.data?.results || [];
        },
        staleTime: 4 * 60 * 60 * 1000, // 4 hours
        refetchOnReconnect: true,
    });
};

// Uncached search for admin tables
export const useVatRateHistorySearch = () => {
    const search = async (params: any) => {
        const response = await apiClient.searchVatRateHistories(params);
        // NSwag generates a shared response wrapper type across multiple search
        // endpoints (see apiClient.ts ApiResponse_searchResponseDTO_1), so the
        // declared type here doesn't reflect the actual VatRateHistoryDTO shape
        // returned at runtime - cast explicitly rather than trust the generated type.
        const data = response?.data as unknown as { results?: VatRateHistoryDTO[]; totalCount?: number } | undefined;
        return {
            results: data?.results || [],
            totalCount: data?.totalCount || 0,
        };
    };

    return { search };
};

// Query by ID
export const useVatRateHistoryById = (id: string) => {
    return useQuery({
        queryKey: ['vatRateHistory', id],
        queryFn: async () => {
            const response = await apiClient.getVatRateHistoryById(id);
            return response?.data || null;
        },
        enabled: !!id,
    });
};

// Create mutation
export const useCreateVatRateHistory = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: VatRateHistoryDTO) => {
            return await apiClient.createVatRateHistory(data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['vatRateHistories'] });
        },
    });
};

// Update mutation
export const useUpdateVatRateHistory = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ id, data }: { id: string; data: VatRateHistoryDTO }) => {
            return await apiClient.updateVatRateHistory(id, data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['vatRateHistories'] });
        },
    });
};

// Delete mutation
export const useDeleteVatRateHistory = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (id: string) => {
            return await apiClient.deleteVatRateHistory(id);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['vatRateHistories'] });
        },
    });
};
